import { createContext, type ReactNode, useContext, useMemo } from "react";

import type { UnderwritableEntitiesV4, UserUwAcknowledgmentsV2 } from "@tycho/web-bindings/types";

import { MESSAGE_KEYS, type MessageKey } from "./messageKeys";

/**
 * Holds the current user's acknowledgments for the active underwritable
 * entity. Consumers (`<AcknowledgmentModal>`, `<OrientationTour>`) read from
 * this context to decide whether to render.
 *
 * Source of truth lives server-side in `user_uw_acknowledgments_v1`; this
 * context just threads the super-view slice through.
 */
type AcknowledgmentsContextValue = {
  acknowledgments: readonly UserUwAcknowledgmentsV2[];
  underwritableEntityId: UnderwritableEntitiesV4["id"];
  isAcked: (key: MessageKey) => boolean;
  // True iff the orientation tour is still pending and `key` isn't itself the
  // orientation key. Centralizes the "tour owns the foreground" rule so the
  // modal and tooltip can't drift.
  shouldDefer: (key: MessageKey) => boolean;
};

const AcknowledgmentsContext = createContext<AcknowledgmentsContextValue | null>(null);

export function useAcknowledgments(): AcknowledgmentsContextValue {
  const ctx = useContext(AcknowledgmentsContext);
  if (!ctx) throw new Error("useAcknowledgments must be used inside <AcknowledgmentsProvider>");
  return ctx;
}

export function AcknowledgmentsProvider({
  underwritableEntityId,
  acknowledgments,
  children,
}: {
  underwritableEntityId: UnderwritableEntitiesV4["id"];
  acknowledgments: readonly UserUwAcknowledgmentsV2[];
  children: ReactNode;
}) {
  const value = useMemo<AcknowledgmentsContextValue>(() => {
    const set = new Set(acknowledgments.map((a) => a.messageKey));
    const isAcked = (key: MessageKey) => set.has(key);
    return {
      acknowledgments,
      underwritableEntityId,
      isAcked,
      shouldDefer: (key) => !isAcked(MESSAGE_KEYS.orientation) && key !== MESSAGE_KEYS.orientation,
    };
  }, [acknowledgments, underwritableEntityId]);

  return (
    <AcknowledgmentsContext.Provider value={value}>{children}</AcknowledgmentsContext.Provider>
  );
}
